import { Utils } from "utils";
import { IPlace, Iterable, IRouteDay, IRouteRequest, IRoute } from "types";
import {
  Path,
  Logger,
  SameRoads,
  RouteTags,
  RouteDays,
  RouteChunk,
  RoutePlaces,
  PathCountries,
  RouteWayponts,
  BestBikingRoad,
  BestBikingRoads,
  DateAvailability,
  GeneralRouteData,
} from "services";

import { buildInitialRoute } from "./helpers/build-initial-route";
import { buildBestBikingRoadsRoute } from "./helpers/build-bbk-route";
import { getNeighborhoodDistanceFor, getSearchRadiusFor } from "./config";
import {
  buildPlacesRoute,
  buildUpdatedPlacesRoute,
} from "./helpers/build-places-route";

enum LoggerKey {
  Route = "Total Algororithm time",
  InitialRoute = "Build Initial Route beween 2 points",
  FetchPlaces = "Fetch places from DB",
  BuildRouteWithPlaces = "Build route with selected places",
  AddBestBikingRoads = "Add Best Biking Roads",
  Days = "Trying to meet selected duration",
  SameRoads = "Find Same Roads",
  AdditionalInfo = "Add Info For Each Day (Suggested places, Same Roads, BBK)",
  DateAvailability = "Check and fix Date Availability for places",
  HotelsAndCamps = "Find Campings for each day",
}

export class Route {
  private _initialPath: Path | undefined = undefined;
  private _placesPath: Path | undefined = undefined;
  private _routeWaypoints: RouteWayponts | undefined = undefined;

  private _bestBikingRoads: BestBikingRoads | undefined = undefined;
  private _selectedBestBikingRoads: BestBikingRoad[] = [];

  private campings: Iterable<IPlace>[] = [];

  // Services

  private logger = new Logger();
  private routePlaces = new RoutePlaces();

  // Varriables

  private start: IRouteRequest["start"];
  private duration: IRouteRequest["duration"];
  private buildVariant: IRouteRequest["buildVariant"];
  private prioritizedTags: IRouteRequest["prioritizedTags"];
  private avoid: IRouteRequest["avoid"];
  private roadType: IRouteRequest["roadType"];
  private finish: IRouteRequest["finish"];
  // private startAsFinish: IRouteRequest["startAsFinish"];
  private radius: IRouteRequest["radius"];
  private tags: IRouteRequest["tags"];

  constructor(props: IRouteRequest) {
    this.start = props.start;
    this.duration = props.duration;
    this.buildVariant = props.buildVariant ?? "linear";
    this.prioritizedTags = props.prioritizedTags;
    this.avoid = props.avoid ?? [];
    this.roadType = props.roadType ?? "calm";
    // this.startAsFinish = props.startAsFinish;
    this.finish = props.startAsFinish ? props.start : props.finish;
    this.radius = props.radius;
    this.tags = props.tags;

    this.routePlaces.selectedTags = this.tags;
    this.routePlaces.prioritizedTags = this.prioritizedTags;
  }

  // Getters

  public get path() {
    return this._placesPath;
  }

  public get bestBikingRoads() {
    return this._bestBikingRoads;
  }

  public get startCoordinate() {
    return this.start?.point;
  }

  public get finishCoordinate() {
    return this.finish?.point;
  }

  public get searchRadius() {
    const value = getSearchRadiusFor(this.duration!.range[1]);
    return value;
  }

  public get neighborhoodDistance() {
    const value = getNeighborhoodDistanceFor(this.duration!.range[1]);
    return value;
  }

  public get selectedBestBikingRoads() {
    return this._selectedBestBikingRoads;
  }

  get allCampings() {
    return this.campings;
  }

  get selectedPlaces() {
    return this._routeWaypoints?.existingPlaces ?? [];
  }

  get routePlacesInstance() {
    return this.routePlaces;
  }

  // Public Methods

  public async getRouteDays(): Promise<IRoute> {
    try {
      let itterateBuilder = true;
      let itteration = 0;
      let isReduced = false;
      let routeDaysData: IRouteDay[] = [];

      const totalTimeLogger = this.logger.create(LoggerKey.Route);
      const daysLoggerItem = this.logger.create(LoggerKey.Days);

      while (itterateBuilder) {
        console.log({ itteration });

        if (this.buildVariant === "linear") await this.buildLinearRoute();
        else await this.buildCircleRoute();

        const coordinates = this._placesPath?.coordinates ?? [];
        const { places = [], waypoints = [] } = this._routeWaypoints ?? {};

        const routeChunk = new RouteChunk(coordinates, places, waypoints);
        routeDaysData = routeChunk.chunk();
        const currentDaysCount = routeDaysData.length;

        const duration = this.duration;
        const [, maxDays] = duration!.range;

        console.log(
          "Current days count: ",
          currentDaysCount,
          "Max days count: ",
          maxDays
        );

        if (currentDaysCount > maxDays) {
          const placesToRemove = await this.routePlaces.reduceOneDay();
          const indexesToRemove = placesToRemove.map((item) =>
            this._routeWaypoints!.findPlaceIndex(item)
          );
          this._routeWaypoints?.removeByIndexes(indexesToRemove);
          const updatedPlaces = this._routeWaypoints!.places ?? [];
          const updatedWaypoints = this._routeWaypoints!.waypoints ?? [];

          const updatedResponse = await buildUpdatedPlacesRoute({
            avoid: this.avoid,
            places: updatedPlaces,
            waypoints: updatedWaypoints,
          });
          this._placesPath = updatedResponse.path;

          const placesIds = updatedResponse.places.map((item) => item?.id);
          this._routeWaypoints = new RouteWayponts({
            places: updatedResponse.places,
            coordinates: updatedResponse.waypoints,
            availablePlaces: this._routeWaypoints?.availablePlaces.filter(
              (item) => !placesIds.includes(item.id)
            ),
          });

          isReduced = true;
          itterateBuilder = true;

          if (itteration === 5) {
            itterateBuilder = false;
          }
        } else {
          itterateBuilder = false;
        }

        itteration += 1;
      }

      this.logger.log(daysLoggerItem);

      const sameRoadsLoggerItem = this.logger.create(LoggerKey.SameRoads);
      const coordinates = this._placesPath?.coordinates ?? [];
      const sameRoads = new SameRoads(coordinates);
      let sameRoadsData = sameRoads.findSameRoads();
      this.logger.log(sameRoadsLoggerItem);

      console.log("------------------");
      console.log("AFTER_ITTERATIONS:");
      console.log({
        place: this._routeWaypoints?.places,
        waypoints: this._routeWaypoints?.waypoints,
      });
      console.log("------------------");

      const availablePlaces = this._routeWaypoints?.availablePlaces ?? [];

      const additionalDataLoggerItem = this.logger.create(
        LoggerKey.AdditionalInfo
      );
      let routeDays = new RouteDays(routeDaysData, availablePlaces);
      routeDays.sameRoads = sameRoadsData;
      routeDays.bestBikingRoads = this.selectedBestBikingRoads;
      routeDays.addAdditionalInfo();
      this.logger.log(additionalDataLoggerItem);

      const dateAvailabilityLoggerItem = this.logger.create(
        LoggerKey.DateAvailability
      );
      const dateAvailability = new DateAvailability(routeDays.data);
      const hasNotAvailable = dateAvailability.hasNotAvailable();
      console.log(`HAS NOT AVAILABLE PLACES: ${hasNotAvailable}`);

      if (hasNotAvailable || isReduced === false) {
        const data = dateAvailability.fixAvailability();

        const updatedRoute = await buildUpdatedPlacesRoute({
          avoid: this.avoid,
          places: data.places,
          waypoints: data.waypoints,
        });
        this._placesPath = updatedRoute.path;

        const newCoordinates = updatedRoute.path.coordinates;
        sameRoadsData = new SameRoads(newCoordinates).findSameRoads();

        const routeChunk = new RouteChunk(
          newCoordinates,
          updatedRoute.places,
          updatedRoute.waypoints
        );
        routeDaysData = routeChunk.chunk();

        const occupiedIds = [...data.places, ...data.removedPlaces].map(
          (i) => i?.id
        );
        const newAvailablePlace = availablePlaces.filter(
          (i) => !occupiedIds.includes(i.id)
        );

        routeDays = new RouteDays(routeDaysData, newAvailablePlace);
        routeDays.sameRoads = sameRoadsData;
        routeDays.bestBikingRoads = this.selectedBestBikingRoads;
        routeDays.addAdditionalInfo();

        this.logger.log(dateAvailabilityLoggerItem);

        const hotelsLoggerItem = this.logger.create(LoggerKey.HotelsAndCamps);
        await routeDays.addHotelsAndCampings();
        routeDaysData = routeDays.data;
        this.logger.log(hotelsLoggerItem);
      } else {
        this.logger.log(dateAvailabilityLoggerItem);

        const hotelsLoggerItem = this.logger.create(LoggerKey.HotelsAndCamps);
        await routeDays.addHotelsAndCampings();
        routeDaysData = routeDays.data;
        this.logger.log(hotelsLoggerItem);
      }

      this.logger.log(totalTimeLogger);

      return Promise.resolve({
        days: routeDaysData,
        logs: this.logger.list,
        sameRoads: sameRoadsData,
        generalData: new GeneralRouteData(
          this.path!,
          routeDaysData,
          sameRoadsData,
          this._bestBikingRoads
        ).generateData(),
      });
    } catch (error) {
      console.log(error);
      return Promise.reject(error);
    }
  }

  // Private Methods

  private async buildLinearRoute() {
    try {
      /*
        1.
        Build Initial Route for start/finish coordinates;
      */

      if (!this.start || !this.finish) {
        return Promise.reject(
          new Error("Please, provide start and end points of route!")
        );
      }

      if (this._initialPath === undefined) {
        const loggerItem = this.logger.create(LoggerKey.InitialRoute);

        const coords = [this.startCoordinate!, this.finishCoordinate!];
        const path = await buildInitialRoute({ coordinates: coords });
        this._initialPath = path;

        this.logger.log(loggerItem);
      }

      /*
        2.
        Fetch places for crossed countries, filter, sort and set breaktimes;
      */

      if (this._routeWaypoints === undefined) {
        const loggerItem = this.logger.create(LoggerKey.FetchPlaces);

        const pathInstance = this._initialPath!.instance;
        const pathCountries = new PathCountries(pathInstance);
        pathCountries.setCoordinates(this.start, this.finish);

        const countries = pathCountries.getCrossedCountryCodes();

        const loggerTagsItem = this.logger.create("Tags");

        const routeTags = new RouteTags(this.tags, countries);
        const selectedTags = await routeTags.get();

        this.tags = selectedTags;
        this.routePlaces.selectedTags = selectedTags;

        this.logger.log(loggerTagsItem);

        // this.routePlaces.selectedTags = this.tags;
        this.routePlaces.countryCodes = countries;
        await this.routePlaces.fetch();

        const coords = this._initialPath!.coordinates;
        const radius = this.searchRadius;
        this.routePlaces.filterAlongCoordinates(coords, radius, [10, 0]);

        this._routeWaypoints = new RouteWayponts({
          places: [],
          coordinates: [],
          availablePlaces: this.routePlaces.getAllPlaces(),
        });

        this.logger.log(loggerItem);
      }

      /*  
          4.
          Rebuild route with selected places;
      */

      if (!this._placesPath) {
        const loggerItem = this.logger.create(LoggerKey.BuildRouteWithPlaces);

        const { daysCount, roadTimeInMinutes } = this.getRouteDurations();
        const response = this.routePlaces.selectForRoute({
          daysCount,
          roadTimeInMinutes,
        });

        const selected = response?.map((item) => item.selected).flat(1) ?? [];
        const selectedPlacesIds = selected.map((item) => item.id);

        const { path, places, waypoints } = await buildPlacesRoute({
          places: selected,
          start: this.startCoordinate!,
          finish: this.finishCoordinate,
        });

        this._placesPath = path;
        this._routeWaypoints.places = places;
        this._routeWaypoints.waypoints = waypoints;
        this._routeWaypoints.availablePlaces =
          this._routeWaypoints.availablePlaces.filter(
            (item) => !selectedPlacesIds.includes(item.id)
          );

        this.logger.log(loggerItem);
      }

      if (this.roadType === "twisty") {
        const loggerItem = this.logger.create(LoggerKey.AddBestBikingRoads);
        await this.getRouteWithBestBikingRoads();
        this.logger.log(loggerItem);
      }

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private async buildCircleRoute() {
    try {
      if (!this.radius || !this.start) {
        return Promise.reject(
          new Error("Please, provide start and end points of route!")
        );
      }

      const { incluedInCircle } = Utils.Countries;
      const radiusInMeters = this.radius! * 1000;

      /* 1. Fetch all places realted to countries and selected tags */

      if (this._routeWaypoints === undefined) {
        const loggerItem = this.logger.create(LoggerKey.FetchPlaces);

        /* 2. Get all country codes that included in circle radius */
        const center = this.startCoordinate!;
        const countries = await incluedInCircle(center, radiusInMeters);
        console.log({ countries, tags: this.tags });

        const loggerTagsItem = this.logger.create("Tags");
        const routeTags = new RouteTags(this.tags, countries);
        const selectedTags = await routeTags.get();

        this.tags = selectedTags;
        this.routePlaces.selectedTags = selectedTags;
        this.routePlaces.countryCodes = countries;

        this.logger.log(loggerTagsItem);

        // this.routePlaces.selectedTags = this._selectedTags;
        await this.routePlaces.fetch();
        this.routePlaces.filterInCircle(this.startCoordinate!, radiusInMeters);

        this._routeWaypoints = new RouteWayponts({
          places: [],
          coordinates: [],
          availablePlaces: this.routePlaces.getAllPlaces(),
        });

        this.logger.log(loggerItem);
      }

      /* 3. Build Places Route  */

      if (this._placesPath === undefined) {
        const loggerItem = this.logger.create(LoggerKey.BuildRouteWithPlaces);

        const { daysCount, roadTimeInMinutes } = this.getRouteDurations();
        const response = this.routePlaces.selectForRoute({
          daysCount,
          roadTimeInMinutes,
        });

        const selected = response.map((item) => item.selected).flat(1);
        const selectedPlacesIds = selected.map((i) => i.id);

        console.log({
          start: this.startCoordinate!,
          finish: this.finishCoordinate,
        });

        const { path, places, waypoints } = await buildPlacesRoute({
          places: selected,
          start: this.startCoordinate!,
          finish: this.finishCoordinate,
        });

        this._placesPath = path;
        this._routeWaypoints.places = places;
        this._routeWaypoints.waypoints = waypoints;
        this._routeWaypoints.availablePlaces =
          this._routeWaypoints.availablePlaces.filter(
            (item) => !selectedPlacesIds.includes(item.id)
          );

        this.logger.log(loggerItem);
      }

      /* 4. If roadType === 'twisty' => add Best Biking Roads */

      if (this.roadType === "twisty") {
        const loggerItem = this.logger.create(LoggerKey.AddBestBikingRoads);
        await this.getRouteWithBestBikingRoads();
        this.logger.log(loggerItem);
      }

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private async getRouteWithBestBikingRoads() {
    try {
      if (this._placesPath && this._routeWaypoints) {
        const { countries, coordinates } = this._placesPath;
        const routeWaypoints = this._routeWaypoints;

        const roadsWaypointsLimit = 76 - routeWaypoints.existingPlaces.length;

        const {
          roads,
          places,
          waypoints,
          path: updatedPath,
          selectedBestBikingRoads: selected,
        } = await buildBestBikingRoadsRoute({
          countries,
          routeWaypoints,
          route: coordinates,
          avoid: this.avoid,
          waypointsCountLimit: roadsWaypointsLimit,
        });

        console.log("BBK_ROUTE");
        console.log({ places, waypoints });

        this._bestBikingRoads = roads;
        this._selectedBestBikingRoads = selected;

        this._placesPath = new Path(updatedPath);

        const selectedPlacesIds = places.map((item) => item?.id);
        this._routeWaypoints = new RouteWayponts({
          places,
          coordinates: waypoints,
          availablePlaces: this._routeWaypoints.availablePlaces.filter(
            (item) => !selectedPlacesIds.includes(item.id)
          ),
        });
      }

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private getRouteDurations() {
    let daysCount = this.duration!.range[1];
    if (this.roadType === "twisty") daysCount = daysCount - 1;
    const roadTimeInMinutes = this._initialPath?.getTime("minutes") ?? 0;

    return { daysCount, roadTimeInMinutes };
  }
}
